import {
  FormPageData,
  IncompleteFormNestedSlot,
  IncompleteFormSelectedSlot,
  PageAPIData,
} from '../../types/types';
import { generateLineItemId } from '../../api/implementations/utils';
import type { Experiments } from '@wix/yoshi-flow-editor';
import {
  SelectedVariants,
  MultiAddonsInfo,
  DynamicPriceInfo,
} from '@wix/bookings-uou-types';
import { getServiceSlotIdentifier } from '..';
import {
  BookedLocation,
  Booking,
} from '../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import {
  V2BookedResource,
  BookedAddOn,
} from '@wix/ambassador-bookings-v2-booking/types';

export type MapBookingsToFormSelectedSlot = {
  bookings: Booking[];
  openSpots: number;
  totalSpots: number;
  timezone: string;
  experiments: Experiments;
};

export const mapBookingsToFormSelectedSlot = ({
  bookings,
  openSpots,
  totalSpots,
  timezone,
  experiments,
}: MapBookingsToFormSelectedSlot): FormPageData => {
  const formSelectedSlot: IncompleteFormSelectedSlot = {
    timezone,
    openSpots,
    totalSpots,
    nestedSlots: generateNestedSlots(bookings),
  };

  const dynamicPricePreSelection = generateDynamicPriceSelection(
    bookings,
    formSelectedSlot,
    experiments,
  );

  const addonsInfoPreSelection = generateAddonsInfoPreSelection(
    bookings,
    formSelectedSlot,
  );

  return {
    timezone,
    formSelectedSlot,
    dynamicPricePreSelection,
    addonsInfoPreSelection,
  };
};

export const generateNestedSlots = (
  bookings: Booking[],
): IncompleteFormNestedSlot[] => {
  return bookings.map((booking): IncompleteFormNestedSlot => {
    const slot = booking?.bookedEntity?.slot || {};

    return {
      lineItemId: generateLineItemId(),
      v2Availability: booking?.v2Availability,
      groupId: booking?.multiServiceBookingInfo?.id,
      startDate: slot.startDate!,
      endDate: slot.endDate!,
      serviceId: slot.serviceId!,
      resource: slot.resource,
      sessionId: slot?.sessionId,
      eventId: slot?.eventId,
      scheduleId: slot.scheduleId,
      timezone: slot.timezone!,
      location: slot?.location,
    };
  });
};

const generateAddonsInfoPreSelection = (
  bookings: Booking[],
  formSelectedSlot: IncompleteFormSelectedSlot,
): MultiAddonsInfo[] => {
  return bookings.reduce<MultiAddonsInfo[]>((acc, curr) => {
    if (curr?.bookedAddOns && curr.bookedEntity?.slot?.serviceId) {
      const relevantSlot = formSelectedSlot.nestedSlots.find((nestedSlot) => {
        return (
          nestedSlot.startDate === curr?.bookedEntity?.slot?.startDate &&
          nestedSlot.serviceId === curr?.bookedEntity?.slot?.serviceId
        );
      });

      if (relevantSlot && relevantSlot.serviceId) {
        const bookedAddOnsByGroupId = curr.bookedAddOns.reduce<
          Record<string, BookedAddOn[]>
        >((accBookedAddOns, addon) => {
          const groupId = addon.groupId!;
          if (!accBookedAddOns[groupId]) {
            accBookedAddOns[groupId] = [];
          }
          accBookedAddOns[groupId].push(addon);
          return accBookedAddOns;
        }, {});

        acc.push({
          serviceId: relevantSlot.serviceId,
          addonsInfo: Object.entries(bookedAddOnsByGroupId).map(
            ([groupId, addons]) => ({
              groupId,
              addons: addons.map((addon) => ({
                id: addon.id!,
                quantity: addon.quantity || 1,
              })),
            }),
          ),
        });
      }
    }
    return acc;
  }, []);
};

export const generateDynamicPriceSelection = (
  bookings: Booking[],
  formSelectedSlot: IncompleteFormSelectedSlot,
  experiments: Experiments,
) => {
  const dynamicPricePreSelection: { [key: string]: DynamicPriceInfo } = {};

  bookings?.reduce((acc, curr) => {
    if (curr?.participantsChoices?.serviceChoices) {
      const relevantSlot = formSelectedSlot.nestedSlots.find((nestedSlot) => {
        if (nestedSlot.startDate && nestedSlot.serviceId) {
          return (
            nestedSlot.startDate === curr?.bookedEntity?.slot?.startDate &&
            nestedSlot.serviceId === curr?.bookedEntity?.slot?.serviceId
          );
        }
        return false; // usually return false when its course
      });

      if (relevantSlot) {
        const selectedVariants: SelectedVariants[] =
          curr.participantsChoices.serviceChoices.map(
            (serviceChoice): SelectedVariants => ({
              numberOfParticipants: serviceChoice.numberOfParticipants || 1,
              choices: serviceChoice.choices,
            }),
          );

        acc[getServiceSlotIdentifier(relevantSlot)] = {
          selectedVariants,
          nonEditable: true,
        };
      }
    }
    return acc;
  }, dynamicPricePreSelection);

  return dynamicPricePreSelection;
};

export const mapPageDataToBookings = (
  pageData: PageAPIData,
): Booking[] | undefined => {
  let bookings: Booking[] | undefined;

  if (pageData?.slotAvailability?.nestedSlots?.length) {
    bookings = pageData.slotAvailability.nestedSlots.map((slot): Booking => {
      const serviceChoices = pageData?.dynamicPricePreSelection?.find(
        (dynamicPrice) =>
          dynamicPrice.serviceId === slot.serviceId &&
          dynamicPrice.startDate === slot.start,
      )?.dynamicPriceInfo?.selectedVariants;

      const bookedAddOns: BookedAddOn[] =
        pageData?.addonsInfoPreSelection
          ?.find((serviceAddons) => serviceAddons.serviceId === slot.serviceId)
          ?.addonsInfo.flatMap((groupAddons) =>
            groupAddons.addons.map((addon) => ({
              groupId: groupAddons.groupId,
              id: addon.id,
              quantity: addon.quantity,
            })),
          ) || [];

      return {
        v2Availability: pageData?.slotAvailability?.isFromV2,
        bookedEntity: {
          slot: {
            eventId: pageData?.slotAvailability?.slot?.eventId ?? undefined,
            startDate: slot.start,
            endDate: slot.end,
            serviceId: slot.serviceId,
            scheduleId: slot.scheduleId,
            resource: slot.resource as V2BookedResource,
            sessionId: pageData?.slotAvailability?.slot?.sessionId,
            timezone: pageData.timezone,
            location: pageData?.slotAvailability?.slot?.location,
          },
        },
        bookedAddOns,
        participantsChoices: {
          serviceChoices,
        },
      };
    });
  } else {
    const serviceChoices = pageData?.dynamicPricePreSelection?.find(
      (dynamicPrice) =>
        dynamicPrice.serviceId ===
          pageData?.slotAvailability?.slot?.serviceId &&
        dynamicPrice.startDate === pageData?.slotAvailability?.slot?.startDate,
    )?.dynamicPriceInfo?.selectedVariants;

    const bookedAddOns: BookedAddOn[] =
      pageData?.addonsInfoPreSelection
        ?.find(
          (serviceAddons) =>
            serviceAddons.serviceId ===
            pageData?.slotAvailability?.slot?.serviceId,
        )
        ?.addonsInfo.flatMap((groupAddons) =>
          groupAddons.addons.map((addon) => ({
            groupId: groupAddons.groupId,
            id: addon.id,
            quantity: addon.quantity,
          })),
        ) || [];

    bookings = [
      {
        v2Availability: pageData?.slotAvailability?.isFromV2,
        bookedEntity: {
          slot: {
            eventId: pageData?.slotAvailability?.slot?.eventId ?? undefined,
            startDate: pageData.slotAvailability?.slot?.startDate!,
            endDate: pageData.slotAvailability?.slot?.endDate!,
            serviceId:
              pageData.slotAvailability?.slot?.serviceId! || pageData.serviceId,
            resource: pageData.slotAvailability?.slot
              ?.resource as V2BookedResource,
            sessionId: pageData.slotAvailability?.slot?.sessionId,
            scheduleId: pageData.slotAvailability?.slot?.scheduleId,
            timezone: pageData.timezone,
            location: pageData.slotAvailability?.slot
              ?.location as unknown as BookedLocation,
          },
        },
        bookedAddOns,
        participantsChoices: {
          serviceChoices,
        },
      },
    ];
  }

  return bookings;
};
